import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import {
	Channel,
	ChannelList,
	DefaultStreamChatGenerics,
	MESSAGE_ACTIONS,
	MessageInput,
	MessageList,
	MessageOptions,
	MessageOptionsProps,
	Thread,
	Window,
	useChatContext,
} from 'stream-chat-react';
import 'stream-chat-react/dist/css/index.css';

import { ChannelFilters } from 'stream-chat';

import Form from '@common/components/Form';
import { checkRole } from '@common/components/RBAC';
import StoreProvider from '@common/components/StoreProvider';
import useModel from '@common/helpers/useModel';
import { useToggle } from '@common/helpers/useToggle';
import ChatGroups from '@common/models/ChatGroups/ChatGroups';
import ChatPrograms from '@common/models/ChatPrograms/ChatPrograms';
import CustomChatInput from '@common/modules/Chat/CustomChatInput';
import MassMailingModal from '@common/modules/Chat/MassMailingModal';
import PinnedMessagePanel from '@common/modules/Chat/PinnedMessagePanel';

import DeletedMessage from './DeletedMessage';
import ChannelListHeader from './Header';
import CustomMessage from './Message';
import CustomQuotedMessage from './Message/QuotedMessage';
import ChatTitle from './Title';
import { TranslationProvider } from './context';
import { ChannelListNavButton, ChannelListWrapper } from './styled';
import customSearchFunction from './utils/customSearch';
import useAutoGrowTextarea from './utils/useAutoGrowTextarea';

function ChatMessageOptions(props: MessageOptionsProps) {
	return <MessageOptions {...props} displayReplies={false} />;
}

function ChatModule({ store, showChannelSearch = true, t }: any) {
	const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
	const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
	const isCurator = checkRole('Curator');

	const massMailingControls = useToggle();

	const { channel, client, openMobileNav } = useChatContext();

	useAutoGrowTextarea();

	const { id: currentUserId } = store.profile;

	const chatProgramsModel = useModel(
		ChatPrograms,
		{ service_id: store.services.current.id },
		[store.services.current],
		{ disableFetch: !isCurator },
	);

	const chatGroupsModel = useModel(
		ChatGroups,
		{ service_id: store.services.current.id },
		[store.services.current],
		{ disableFetch: !isCurator },
	);
	const { userId: currentCgUserId } = store.chat;

	const currentChatUserId = currentCgUserId ?? currentUserId;
	const filters = {
		$and: [
			{
				$or: [{ type: 'messaging' }, { type: 'channel' }, { type: 'group' }],
			},
			{
				$or: [
					{ service_ids: { $contains: store.services.current.id } },
					{ service_ids: { $contains: `${store.services.current.id}` } },
				],
			},
			{ members: { $in: [`${currentChatUserId}`] } },

			...(selectedGroups.length
				? [
						{
							$or: [
								{ ks_group_id: { $in: selectedGroups } },
								{ ks_group_ids: { $in: selectedGroups } },
							],
						},
					]
				: []),

			...(selectedPrograms.length
				? [
						{
							$or: [
								{ ks_program_id: { $in: selectedPrograms } },
								{ ks_program_ids: { $in: selectedPrograms } },
							],
						},
					]
				: []),
		],
	} as ChannelFilters<DefaultStreamChatGenerics>;

	const showDateSeparator = (channel?.state?.messages || []).some(
		(message) => message.type !== 'deleted',
	);
	const messageActions = Object.values(MESSAGE_ACTIONS).filter(
		(action) => action !== MESSAGE_ACTIONS.reply,
	);

	const MemoizedMessageDeleted = useCallback(
		(props: any) => <DeletedMessage currentUserId={currentChatUserId} {...props} />,
		[currentChatUserId],
	);

	return (
		<TranslationProvider t={t}>
			<StoreProvider store={store}>
				<ChannelListWrapper>
					<Form initialValues={{ group: [], program: [] }} hideActions>
						<ChatTitle
							chatProgramsModel={chatProgramsModel}
							chatGroupsModel={chatGroupsModel}
							onGroupsChange={setSelectedGroups}
							onProgramsChange={setSelectedPrograms}
							openMassMailingModal={() => massMailingControls.onOpen()}
						/>
					</Form>

					<ChannelListNavButton variant="text" color="primary" onClick={openMobileNav}>
						All chat
					</ChannelListNavButton>

					<ChannelList
						sort={{ last_updated: -1 }}
						filters={filters}
						options={{ limit: 0 }}
						showChannelSearch={showChannelSearch}
						additionalChannelSearchProps={{
							searchForChannels: true,
							searchFunction: (params, event) =>
								customSearchFunction(params, event, client, currentChatUserId, filters),
						}}
					/>
				</ChannelListWrapper>

				<Channel
					MessageDeleted={MemoizedMessageDeleted}
					MessageOptions={ChatMessageOptions}
					QuotedMessage={CustomQuotedMessage}
				>
					<Window>
						<ChannelListHeader />

						<PinnedMessagePanel />

						<MessageList
							Message={CustomMessage}
							disableDateSeparator={!showDateSeparator}
							messageActions={messageActions}
						/>

						{/* @ts-ignore */}
						{channel?.data?.own_capabilities?.includes('send-message') && (
							<MessageInput Input={CustomChatInput} />
						)}
					</Window>

					<Thread />
					{massMailingControls.isOpen && <MassMailingModal onClose={massMailingControls.onClose} />}
				</Channel>
			</StoreProvider>
		</TranslationProvider>
	);
}

export default observer(ChatModule);
